import type React from 'react';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { SegmentedControl, Switch } from '@mantine/core';
import clsx from 'clsx';

import type { Dimensions } from '../../../../../prop-types/dimension-types';

import { CheckboxSlider } from 'ui-components/components/checkbox-slider';
import { CurrencyInput } from 'ui-components/components/currency-input';
import { DimensionInput } from 'ui-components/components/dimension-input';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { InfoIcon } from 'ui-components/components/info-icon';
import { InlineMessage, INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { Input, version2 } from 'ui-components/components/input';
import { Text, TextType } from 'ui-components/components/text';
import { WeightInput } from 'ui-components/components/weight-input';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { BYOLRestrictionsBanner } from '../../../../../components/byol-banner';
import { DangerousGoodsForm } from '../../../../../components/dangerous-goods/dangerous-goods-form';
import type { DangerousGoodsFormType } from '../../../../../components/dangerous-goods/dangerous-goods-form';
import { MsdsUpload } from '../../../../../components/dangerous-goods/msds-upload/msds-upload';
import { UnitPosition } from '../../../../../enums/units/unit-position';
import type { UnitPosition as UnitPositionType } from '../../../../../enums/units/unit-display-types';
import { useEnableDangerousGoods } from '../../../../../feature-flags/hooks/use-enable-dangerous-goods';
import { useEnableProductItemWeightAndDimensions } from '../../../../../feature-flags/hooks/use-enable-product-item-weight-and-dimensions';
import { useEnableLtlFreight } from '../../../../../feature-flags/hooks/use-enable-ltl-freight';
import { useEnableMultipackagePhase2 } from '../../../../../feature-flags/hooks/use-enable-multipackage-phase-2';
import { HazmatSection } from './components/hazmat-section/hazmat-section';
import { PackageSetSection } from './components/package-set-section/package-set-section';
import { useEnableFulfillmentRates } from '../../../../../feature-flags/hooks/use-enable-fulfillment-rates';
import { useIsAutoSplitEnabledForSeller } from '../../../../../hooks/use-is-auto-splitting-enabled-for-seller';
import { useIsDeliveryOptionsAnz } from '../../../../../pages/settings/stores/selected-store/delivery-options-settings/hooks';
import Global from '../../../../../store/global-selectors';
import { BundleContext } from '../../../bundle-context';
import { useProductDetailsContext } from '../../../use-product-details-context';
import { messages } from '../../../messages';
import style from './shipping-defaults.module.css';
import { AutoSplitRadioButtons } from '../../auto-split-radio-buttons';
import { ProductDetailsDimensionsPresetGroupDisplay } from '../../dimensions-inherited-display';
import { ProductDetailsEnabledCascadingDropdowns } from '../../enabled-cascading-dropdowns';
import { PackagingOptionsDropdown } from '../../packaging-options-dropdown/packaging-options-dropdown';
import { ProductDetailsPresetGroupDropdown } from '../../preset-group-dropdown';
import { ProductDetailsWeightPresetGroupDisplay } from '../../weight-inherited-display';

type MsdsFile = {
  fileName: string;
  uploadDate: string;
};

type DimsSource = 'package' | 'item';

const computeDefaultDimsSource = (
  packageWeight: number,
  packageDims: { length: number; width: number; height: number },
  itemWeight: number,
  itemDims: { length: number; width: number; height: number },
): DimsSource => {
  const packageScore = packageWeight + packageDims.length + packageDims.width + packageDims.height;
  const itemScore = itemWeight + itemDims.length + itemDims.width + itemDims.height;
  if (itemScore > 0 && packageScore > 0) {
    return itemScore > packageScore ? 'item' : 'package';
  }
  if (itemScore > 0) return 'item';
  return 'package';
};

const ZERO_USD = { value: 0, code: 'USD' };

export const ProductDetailsShippingDefaultsV3 = () => {
  const {
    productId,
    userHasPermission,
    weightValue: productWeightValue,
    setWeightValue,
    weightUnit: productWeightUnit,
    productWarehouseLocation,
    setProductWarehouseLocation,
    productFulfillmentSku,
    setProductFulfillmentSku,
    dimensions,
    setDimensions,
    sizeUnit,
    autoSplitOption,
    setAutoSplitOption,
    autoSplitCustomQuantity,
    setAutoSplitCustomQuantity,
    productPrice,
    setProductPrice,
    enableProductPrice,
    setEnableProductPrice,
    productDangerousGoods,
    setProductDangerousGoods,
    productMsdsFile,
    setProductMsdsFile,
    validationErrors,
    productItemWeight,
    setProductItemWeight,
    productItemDimensions,
    setProductItemDimensions,
    isMultiPackageEnabled,
    setIsMultiPackageEnabled,
  } = useProductDetailsContext();
  const { isBundle, componentsWeight } = useContext(BundleContext);
  const intl = useIntl();

  const fulfillmentRatesEnabled = useEnableFulfillmentRates();
  const isAutoSplitEnabled = useIsAutoSplitEnabledForSeller();
  const weightValue = isBundle ? componentsWeight?.value : productWeightValue;
  const weightUnit = isBundle ? componentsWeight?.unit : productWeightUnit;
  const isDeliveryOptionsAnz = useIsDeliveryOptionsAnz();
  const isDangerousGoodsOptionEnabled = useEnableDangerousGoods();
  const isLtlFreightEnabled = useEnableLtlFreight();
  const isMultipackagePhase2Enabled = useEnableMultipackagePhase2();

  const isDgSectionOn = !!productDangerousGoods?.dgSectionEnabled;
  const isHazmatSectionOn = !!productDangerousGoods?.hazmatSectionEnabled;

  // Auto-split: local state controls panel visibility, independent of the selected option
  const [isAutoSplitPanelOpen, setIsAutoSplitPanelOpen] = useState(!!autoSplitOption && autoSplitOption !== 'DoNotAutoSplit');

  const onAutoSplitToggle = useCallback(
    (checked: boolean) => {
      setIsAutoSplitPanelOpen(checked);
      if (!checked) {
        setAutoSplitOption('DoNotAutoSplit');
      }
    },
    [setAutoSplitOption],
  );

  // Dangerous goods toggle
  const onToggleDangerousGoods = useCallback(
    (on: boolean) => {
      if (on) {
        // dangerousAmount default comes first so existing values from the spread override it
        setProductDangerousGoods({
          dangerousAmount: { amount: 0, unit: 'g' },
          ...productDangerousGoods,
          dgSectionEnabled: true,
        });
      } else if (productDangerousGoods?.hazmatSectionEnabled) {
        setProductDangerousGoods({ ...productDangerousGoods, dgSectionEnabled: false });
      } else {
        setProductDangerousGoods(null);
      }
    },
    [setProductDangerousGoods, productDangerousGoods],
  );

  // HAZMAT toggle
  const onToggleHazmat = useCallback(
    (on: boolean) => {
      if (on) {
        setProductDangerousGoods({ ...productDangerousGoods, hazmatSectionEnabled: true });
      } else if (productDangerousGoods?.dgSectionEnabled) {
        setProductDangerousGoods({ ...productDangerousGoods, hazmatSectionEnabled: false });
      } else {
        setProductDangerousGoods(null);
      }
    },
    [setProductDangerousGoods, productDangerousGoods],
  );

  // Fulfillment rates — inline CurrencyInput (logic from ProductFee)
  const [currencyInputValue, setCurrencyInputValue] = useState(productPrice ?? ZERO_USD);
  const [isProductFeeOverLimit, setIsProductFeeOverLimit] = useState((productPrice?.value ?? 0) > 9999999.99);
  const currencyCode = useSelector(Global.selectSellerDefaultCurrency);

  const isProductItemWeightAndDimensionsEnabled = useEnableProductItemWeightAndDimensions();

  const [dimsSource, setDimsSource] = useState<DimsSource>(() =>
    computeDefaultDimsSource(
      weightValue ?? 0,
      { length: dimensions?.length ?? 0, width: dimensions?.width ?? 0, height: dimensions?.height ?? 0 },
      productItemWeight.value ?? 0,
      { length: productItemDimensions.length ?? 0, width: productItemDimensions.width ?? 0, height: productItemDimensions.height ?? 0 },
    ),
  );

  const dimsSourceOptions = useMemo(
    () => [
      { value: 'package', label: intl.formatMessage(messages.dimsSourcePackageOption) },
      { value: 'item', label: intl.formatMessage(messages.dimsSourceItemOption) },
    ],
    [intl],
  );

  const prevPackageRef = useRef({ weightValue: weightValue ?? 0, dimensions: dimensions ?? { length: 0, width: 0, height: 0 } });
  const prevItemRef = useRef({ productItemWeight, productItemDimensions });

  const handleDimsSourceChange = useCallback(
    (value: string) => {
      if (isBundle) return;
      const next = value as DimsSource;
      setDimsSource(next);
      if (next === 'package') {
        prevItemRef.current = { productItemWeight, productItemDimensions };
        const prev = prevPackageRef.current;
        setWeightValue(prev.weightValue);
        setDimensions(prev.dimensions);
        setProductItemWeight({ value: 0, unit: productItemWeight.unit });
        setProductItemDimensions({ length: 0, width: 0, height: 0, unit: productItemDimensions.unit ?? sizeUnit ?? null });
      } else {
        prevPackageRef.current = { weightValue: weightValue ?? 0, dimensions: dimensions ?? { length: 0, width: 0, height: 0 } };
        const prev = prevItemRef.current;
        setProductItemWeight(prev.productItemWeight);
        setProductItemDimensions(prev.productItemDimensions);
        setWeightValue(0);
        setDimensions({ length: 0, width: 0, height: 0 });
      }
    },
    [
      isBundle,
      weightValue,
      dimensions,
      productItemWeight,
      productItemDimensions,
      sizeUnit,
      setWeightValue,
      setDimensions,
      setProductItemWeight,
      setProductItemDimensions,
    ],
  );

  const onFulfillmentRatesToggle = useCallback(
    (checked: boolean) => {
      setEnableProductPrice(checked);
    },
    [setEnableProductPrice],
  );

  const onProductFeeValueChange = useCallback(
    (updatedCurrencyValue: string | number | null | undefined) => {
      const numericValue = updatedCurrencyValue ? Number(updatedCurrencyValue) : 0.0;
      const newValue = { value: numericValue, code: currencyCode };
      setCurrencyInputValue(newValue);
      setIsProductFeeOverLimit(numericValue > 9999999.99);
    },
    [currencyCode],
  );

  const onProductFeeBlur = useCallback(() => {
    const newProductPrice = currencyInputValue.value
      ? { value: Number(currencyInputValue.value), code: currencyCode }
      : { value: 0.0, code: currencyCode };
    setProductPrice(newProductPrice);
  }, [setProductPrice, currencyInputValue, currencyCode]);

  return (
    <div className={style['shipping-tab']}>
      {/* === Preset group + Shipping preferences card === */}
      <div className={style['content-card']}>
        {/* Preset */}
        <div className={style['preset-row']}>
          <ProductDetailsPresetGroupDropdown />
        </div>

        {/* BYOL banner (conditional, shown when applicable) */}
        <div className={style['byol-banner']}>
          <BYOLRestrictionsBanner contentText={messages.BYOLRestrictionsBannerText} />
        </div>

        <div className={style.section}>
          {/* Shipping preferences */}
          <Text element='div' textType={TextType.H4} value={messages.shippingPreferencesTitle} />
          <div className={style['shipping-options']}>
            <Text className={style['shipping-option-label']} element='div' textType={TextType.Label} value={messages.defaultServiceName} />
            <Text className={style['shipping-option-label']} element='div' textType={TextType.Label} value={messages.defaultPackageName} />
            <Text className={style['shipping-option-label']} element='div' textType={TextType.Label} value={messages.defaultConfirmation} />
            <ProductDetailsEnabledCascadingDropdowns />
            <Text
              className={style['shipping-option-label']}
              element='div'
              textType={TextType.Label}
              value={messages.defaultInternationalServiceName}
            />
            <Text
              className={style['shipping-option-label']}
              element='div'
              textType={TextType.Label}
              value={messages.defaultInternationalPackageName}
            />
            <Text
              className={style['shipping-option-label']}
              element='div'
              textType={TextType.Label}
              value={messages.defaultInternationalConfirmation}
            />
            <ProductDetailsEnabledCascadingDropdowns isInternational />
          </div>
        </div>
      </div>

      {isMultipackagePhase2Enabled ? (
        <div className={style['toggle-card']}>
          <Switch
            size='sm'
            checked={isMultiPackageEnabled}
            disabled={!userHasPermission}
            label={intl.formatMessage(messages.shipsInMultiplePackagesLabel)}
            onChange={(event) => setIsMultiPackageEnabled(event.currentTarget.checked)}
            description={intl.formatMessage(messages.shipsInMultiplePackagesCaption)}
          />
          {isMultiPackageEnabled ? (
            <div className={style['expanded-content']}>
              <PackageSetSection />
            </div>
          ) : null}
        </div>
      ) : null}

      {/* === Dimensions card === */}
      <Text className={style['section-title']} element='div' textType={TextType.H4} value={messages.dimensionsTitle} />
      {isProductItemWeightAndDimensionsEnabled ? (
        <SegmentedControl data={dimsSourceOptions} fullWidth onChange={handleDimsSourceChange} value={dimsSource} />
      ) : null}
      <FlexRow className={style['location-dimensions-row']} gap={Gap.Space12}>
        {/* Package Dimensions card */}
        <div
          className={clsx(style['dimensions-card'], {
            [style['dimensions-card--disabled']]: isProductItemWeightAndDimensionsEnabled && dimsSource === 'item',
          })}
        >
          <div>
            <FlexRow className={style.label} gap={Gap.Space4}>
              <Text textType={TextType.Label} value={messages.packageWeight} />
              {isBundle ? (
                <InfoIcon
                  color={THEME_CONSTANTS.unstyled}
                  tooltipText={<Text textType={TextType.Caption} value={messages.bundleComponentWeight} />}
                />
              ) : null}
            </FlexRow>
            <div className={style['weight-wrapper']}>
              <WeightInput
                disabled={!userHasPermission || isBundle}
                inputClassName={style['weight-input']}
                onChange={setWeightValue}
                unit={weightUnit}
                unitPosition={UnitPosition.InsideStart as UnitPositionType}
                weight={weightValue || 0}
              />
              <ProductDetailsWeightPresetGroupDisplay />
            </div>
          </div>
          <div>
            <FlexRow className={style.label} gap={Gap.Space4}>
              <Text textType={TextType.Label} value={messages.packageDimensions} />
              {isDeliveryOptionsAnz ? (
                <InfoIcon tooltipText={<Text textType={TextType.Caption} value={messages.shippingDefaultsTooltip} />} />
              ) : null}
            </FlexRow>
            <div className={style['size-wrapper']}>
              <DimensionInput
                dimensions={dimensions as Dimensions}
                disabled={!userHasPermission}
                inputClassName={style['size-input']}
                onChange={setDimensions}
                unit={sizeUnit}
                unitPosition={UnitPosition.InsideStart as UnitPositionType}
              />
              <ProductDetailsDimensionsPresetGroupDisplay />
            </div>
          </div>
        </div>

        {/* Item Dimensions card */}
        <div
          className={clsx(style['dimensions-card'], {
            [style['dimensions-card--disabled']]: isProductItemWeightAndDimensionsEnabled && dimsSource === 'package',
          })}
        >
          <div>
            <Text className={style.label} element='div' textType={TextType.Label} value={messages.packageItemWeight} />
            <div className={style['weight-wrapper']}>
              <WeightInput
                disabled={!userHasPermission || isBundle}
                inputClassName={style['weight-input']}
                onChange={(weight: number, unit: string) =>
                  setProductItemWeight({
                    value: weight,
                    unit: unit || productItemWeight.unit || weightUnit,
                  })
                }
                unit={productItemWeight.unit || weightUnit}
                unitPosition={UnitPosition.InsideStart as UnitPositionType}
                weight={productItemWeight.value || 0}
              />
            </div>
          </div>
          <div>
            <Text className={style.label} element='div' textType={TextType.Label} value={messages.packageItemDimensions} />
            <div className={style['size-wrapper']}>
              <DimensionInput
                dimensions={productItemDimensions as Dimensions}
                disabled={!userHasPermission}
                inputClassName={style['size-input']}
                onChange={(dims: Dimensions) =>
                  setProductItemDimensions({
                    ...dims,
                    unit: dims.unit ?? productItemDimensions.unit ?? sizeUnit ?? null,
                  })
                }
                unit={productItemDimensions.unit || sizeUnit}
                unitPosition={UnitPosition.InsideStart as UnitPositionType}
              />
            </div>
          </div>
        </div>
      </FlexRow>

      {/* === Packaging card === */}
      <Text className={style['section-title']} element='div' textType={TextType.H4} value={messages.packagingTitle} />
      <div className={style['packaging-card']}>
        <PackagingOptionsDropdown className={style['packaging-grid']} disabled={!userHasPermission} />
      </div>

      {/* === Location card === */}
      <Text className={style['section-title']} element='div' textType={TextType.H4} value={messages.locationTitle} />
      <div className={style['location-card']}>
        <div>
          <Text className={style.label} element='div' textType={TextType.Label} value={messages.warehouseLocation} />
          <Input
            className={style['input-wrapper']}
            data-testid='warehouse-location'
            disabled={!userHasPermission}
            inlineMessage={validationErrors?.warehouseLocation?.message}
            inlineMessageType={validationErrors?.warehouseLocation?.message ? INLINE_MESSAGE_TYPES.error : undefined}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setProductWarehouseLocation(event.target.value)}
            value={productWarehouseLocation}
            version={version2}
          />
        </div>
        <div>
          <Text className={style.label} element='div' textType={TextType.Label} value={messages.fulfillmentSku} />
          <Input
            className={style['input-wrapper']}
            data-testid='fulfillment-sku'
            disabled={!userHasPermission}
            onChange={setProductFulfillmentSku}
            value={productFulfillmentSku}
          />
        </div>
      </div>

      {/* === TOGGLE CARDS (each in its own bordered card) === */}
      {isAutoSplitEnabled ? (
        <div className={style['toggle-card']}>
          <CheckboxSlider
            checkboxId={`${productId}-auto-split`}
            checked={isAutoSplitPanelOpen}
            disabled={!userHasPermission}
            labelText={intl.formatMessage(messages.enableAutoSplitLabel)}
            onChange={onAutoSplitToggle}
          />
          {isAutoSplitPanelOpen ? (
            <div className={style['expanded-content']}>
              <AutoSplitRadioButtons
                currentQuantity={autoSplitCustomQuantity}
                disabled={!userHasPermission}
                onChange={setAutoSplitOption}
                selectedSplitOption={autoSplitOption || 'DoNotAutoSplit'}
                setAutoSplitCustomQty={setAutoSplitCustomQuantity}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {fulfillmentRatesEnabled ? (
        <div className={style['toggle-card']}>
          <CheckboxSlider
            checkboxId={`${productId}-fulfillment-rates`}
            checked={enableProductPrice}
            disabled={!userHasPermission}
            labelText={intl.formatMessage(messages.enableFulfillmentRatesLabel)}
            onChange={onFulfillmentRatesToggle}
          />
          <Text className={style.caption} element='div' textType={TextType.Caption} value={messages.enableFulfillmentRatesCaption} />
          {enableProductPrice ? (
            <div className={style['expanded-content']}>
              <Text className={style.label} element='div' textType={TextType.Label} value={messages.productFee} />
              <div className={style['product-fee-input']}>
                <CurrencyInput
                  currencyCode={currencyCode}
                  disabled={!userHasPermission}
                  onBlur={onProductFeeBlur}
                  onChange={onProductFeeValueChange}
                  showPlaceHolderForZero
                  value={currencyInputValue.value}
                />
                {isProductFeeOverLimit ? (
                  <InlineMessage type={INLINE_MESSAGE_TYPES.error}>
                    <Text textType={TextType.Caption} value={messages.productFeeOverLimit} />
                  </InlineMessage>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {isDangerousGoodsOptionEnabled ? (
        <div className={style['toggle-card']}>
          <CheckboxSlider
            checkboxId={`${productId}-dangerous-goods`}
            checked={isDgSectionOn}
            disabled={!userHasPermission}
            labelText={intl.formatMessage(messages.markDangerousGoodLabel)}
            onChange={onToggleDangerousGoods}
          />
          {isDgSectionOn && productDangerousGoods ? (
            <div className={style['expanded-content']}>
              <FlexColumn gap={Gap.Space16}>
                <DangerousGoodsForm
                  onChange={setProductDangerousGoods as unknown as (arg: unknown) => void}
                  value={productDangerousGoods as DangerousGoodsFormType}
                />
                {Boolean(productId) && (
                  <MsdsUpload
                    currentMsdsFile={productMsdsFile as MsdsFile | null}
                    isDisabled={!userHasPermission}
                    productId={String(productId!)}
                    setCurrentMsdsFile={setProductMsdsFile}
                  />
                )}
              </FlexColumn>
            </div>
          ) : null}
        </div>
      ) : null}

      {isLtlFreightEnabled ? (
        <>
          <div className={style['hazmat-section-header']}>
            <div className={style['hazmat-title']}>
              <Text element='span' textType={TextType.H4} value={messages.hazmatSectionTitle} />
              <InfoIcon tooltipText={intl.formatMessage(messages.hazmatSectionInfoTooltip)} />
            </div>
          </div>
          <div className={style['toggle-card']}>
            <CheckboxSlider
              checkboxId={`${productId}-hazmat`}
              checked={isHazmatSectionOn}
              disabled={!userHasPermission}
              labelText={intl.formatMessage(messages.markHazmatLabel)}
              onChange={onToggleHazmat}
            />
            <div className={style.caption}>
              <Text textType={TextType.Paragraph} value={messages.hazmatToggleCaption} />
            </div>
            {isHazmatSectionOn ? (
              <div className={style['hazmat-expanded']}>
                <HazmatSection />
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
};
